// src/pages/admin/SuperAdminUsersPage.js
// Updated: 11/09/2025 08:56 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect, useCallback } from 'react';
import { auth, functions } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, Navigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { Shield, Search, ArrowUp, ArrowDown, UserCheck, CheckCircle, XCircle, ShieldCheck } from 'lucide-react';

// Prepare the Cloud Functions
const superAdminGetUserList = httpsCallable(functions, 'superAdminGetUserList');
const superAdminImpersonateUser = httpsCallable(functions, 'superAdminImpersonateUser');
const superAdminGetRevertToken = httpsCallable(functions, 'superAdminGetRevertToken');
const adminManuallyVerifyUser = httpsCallable(functions, 'adminManuallyVerifyUser');

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

// Custom hook for handling input delay (debouncing)
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

export default function SuperAdminUsersPage() {
  const { userData, currentUser, logout, isLoading: isAuthLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // State for sorting and searching
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'email', direction: 'asc' });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [isImpersonatingId, setIsImpersonatingId] = useState(null);
  const [isUpdatingId, setIsUpdatingId] = useState(null);
  const navigate = useNavigate();

  // Data fetching function (No changes)
  const fetchUsers = useCallback(async (search, sort) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await superAdminGetUserList({
        sortBy: sort.key,
        sortDirection: sort.direction,
        searchTerm: search,
      });
      setUsers(result.data.users);
    } catch (err) {
      console.error("Error fetching user list:", err);
      setError(err.message || "Failed to fetch users. Are you a Super Admin?");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to re-fetch (No changes)
  useEffect(() => {
    if (!isAuthLoading && userData?.super_admin === true) {
      fetchUsers(debouncedSearchTerm, sortConfig);
    }
  }, [debouncedSearchTerm, sortConfig, fetchUsers, isAuthLoading, userData]);

  // Handle clicking on table headers to sort (No changes)
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ArrowUp className="inline h-4 w-4" /> : <ArrowDown className="inline h-4 w-4" />;
  };

  // --- 1. UPDATE Handler for impersonation ---
  const handleImpersonate = async (userIdToImpersonate) => {
    if (!currentUser) return;

    setIsImpersonatingId(userIdToImpersonate);
    setError('');

    try {
      const revertTokenResult = await superAdminGetRevertToken();
      const revertToken = revertTokenResult.data.token;
      if (!revertToken) {
        throw new Error("Could not get a revert token.");
      }

      // --- 2. Pass the admin's orgId to the impersonation function ---
      // Although the function logs the *target's* org,
      // we store the *admin's* orgId for the "stop" log.
      const impersonationTokenResult = await superAdminImpersonateUser({
        userIdToImpersonate: userIdToImpersonate,
      });
      const impersonationToken = impersonationTokenResult.data.token;
      if (!impersonationToken) {
        throw new Error("No impersonation token returned from function.");
      }

      // --- 3. STORE all necessary info in session storage ---
      sessionStorage.setItem('impersonating_admin_id', currentUser.uid);
      sessionStorage.setItem('impersonation_revert_token', revertToken);
      // Store the admin's orgId to log the 'stop' event later
      sessionStorage.setItem('impersonating_admin_org_id', userData.orgId);
      // --- END OF UPDATES ---

      await logout();
      await signInWithCustomToken(auth, impersonationToken);
      navigate('/dashboard');

    } catch (err) {
      console.error("Error during impersonation:", err);
      setError(err.message || "Failed to impersonate user.");
      sessionStorage.removeItem('impersonating_admin_id');
      sessionStorage.removeItem('impersonation_revert_token');
      sessionStorage.removeItem('impersonating_admin_org_id'); // Clear all
      setIsImpersonatingId(null);
    }
  };

  // Handler for manual verification (No changes)
  const handleManualVerify = async (userIdToVerify, userName) => {
    if (!window.confirm(`Are you sure you want to manually verify the email for ${userName}?`)) {
      return;
    }
    setIsUpdatingId(userIdToVerify);
    setStatusMessage(`Verifying ${userName}...`);
    setError('');
    try {
      const result = await adminManuallyVerifyUser({ userIdToVerify });
      console.log(result.data.message);
      setStatusMessage(`${userName} has been manually verified.`);
      setUsers(users.map(u => u.id === userIdToVerify ? { ...u, emailVerified: true } : u));
    } catch (err) {
      console.error("Manual verification error:", err);
      setError(err.message || "Failed to verify user.");
      setStatusMessage('');
    }
    setIsUpdatingId(null);
    setTimeout(() => { setStatusMessage(''); setError(''); }, 5000);
  };

  // --- No changes to the rest of the file ---
  if (!isAuthLoading && userData?.super_admin !== true) {
    console.warn("Access Denied: User is not a Super Admin.");
    return <Navigate to="/dashboard" replace />;
  }
  if (isAuthLoading || (isLoading && users.length === 0)) {
    return <LoadingSpinner />;
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-lg">
      <h1 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
        <Shield className="mr-3 h-8 w-8 text-purple-600" />
        Super Admin: Master User List
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Showing all users across all organizations. Use with caution.
      </p>
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Search by Name or Email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-md border-gray-300 pl-10 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
      </div>
      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {statusMessage && (
        <div className="mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-700">
          {statusMessage}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('displayName')}
              >
                Name {getSortIcon('displayName')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('email')}
              >
                Email {getSortIcon('email')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('orgId')}
              >
                Organization {getSortIcon('orgId')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('role')}
              >
                Role {getSortIcon('role')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Email Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading && users.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const isCurrentUser = user.id === currentUser?.uid;
                const isVerified = user.emailVerified === true;

                return (
                  <tr key={user.id}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {user.displayName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500" title={user.orgId}>
                      {user.orgName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {user.role}
                      {user.super_admin && (
                        <span className="ml-2 text-xs text-purple-600">(Super)</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {isVerified ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-0.5 text-xs font-medium text-green-800">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-0.5 text-xs font-medium text-red-800">
                          <XCircle className="mr-1 h-3 w-3" />
                          Not Verified
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm space-x-2">
                      <button
                        onClick={() => handleImpersonate(user.id)}
                        disabled={isCurrentUser || !!isImpersonatingId || !!isUpdatingId}
                        className="flex items-center rounded bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 shadow-sm hover:bg-blue-200 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                        title={isCurrentUser ? "Cannot impersonate yourself" : `Impersonate ${user.displayName}`}
                      >
                        {isImpersonatingId === user.id ? (
                          'Logging in...'
                        ) : (
                          <>
                            <UserCheck className="mr-1 h-4 w-4" />
                            Impersonate
                          </>
                        )}
                      </button>
                      {!isVerified && (
                        <button
                          onClick={() => handleManualVerify(user.id, user.displayName)}
                          disabled={isCurrentUser || !!isImpersonatingId || !!isUpdatingId}
                          className="flex items-center rounded bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 shadow-sm hover:bg-yellow-200 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                          title={`Manually verify ${user.displayName}`}
                        >
                          {isUpdatingId === user.id ? (
                            'Verifying...'
                          ) : (
                            <>
                              <ShieldCheck className="mr-1 h-4 w-4" />
                              Verify
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}